<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import type { Address } from "@/components/controls/address/addresses";
import { formatAddressForDisplay } from "@/components/controls/address/addresses";
import BmAddressCaptureDialog from "@/components/controls/address/BmAddressCaptureDialog.vue";
import BmControl from "@/components/controls/input/BmControl.vue";
import BmAddressLookup from "@/components/controls/address/BmAddressLookup.vue";
import { computed, ref, watch } from "vue";
import dataComponentAttrs from "@/components/controls/DataComponentAttrs";

/**
 * A control that encapsulates address entry - and an optional business name.
 *
 * Gives the user an edit option that invokes a dialog to capture new address
 * information.
 */

const emit = defineEmits<{
  (
    event: "update:address-business-name",
    businessName: string | undefined
  ): void;
  (event: "update:address-line1", addressLine1: string): void;
  (event: "update:address-line2", addressLine2: string): void;
  (event: "update:address-line3", addressLine3: string): void;
  (event: "update:address-line4", addressLine4: string): void;
  (event: "update:address-line5", addressLine5: string): void;
  (event: "update:address-line6", addressLine6: string): void;
  (event: "update:address-postcode", addressPostcode: string): void;
}>();

interface Props {
  addressBusinessName?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  addressLine5?: string;
  addressLine6?: string;
  addressPostcode?: string;
  allowManualInput?: boolean;
}

const props = withDefaults(defineProps<Props>(), { allowManualInput: true });

const showAddressCard = ref(true);

const address = computed<Address>(() => {
  return {
    addressBusinessName: props.addressBusinessName ?? undefined,
    addressLine1: props.addressLine1 ?? "",
    addressLine2: props.addressLine2 ?? "",
    addressLine3: props.addressLine3 ?? "",
    addressLine4: props.addressLine4 ?? "",
    addressLine5: props.addressLine5 ?? "",
    addressPostcode: props.addressPostcode ?? "",
    addressLine6: props.addressLine6 ?? "",
  };
});

const emitChangedFields = (newValue: Address) => {
  if (newValue.addressBusinessName !== props.addressBusinessName) {
    emit("update:address-business-name", newValue.addressBusinessName);
  }

  if (newValue.addressLine1 !== props.addressLine1) {
    emit("update:address-line1", newValue.addressLine1);
  }

  if (newValue.addressLine2 !== props.addressLine2) {
    emit("update:address-line2", newValue.addressLine2);
  }

  if (newValue.addressLine3 !== props.addressLine3) {
    emit("update:address-line3", newValue.addressLine3);
  }

  if (newValue.addressLine4 !== props.addressLine4) {
    emit("update:address-line4", newValue.addressLine4);
  }

  if (newValue.addressLine5 !== props.addressLine5) {
    emit("update:address-line5", newValue.addressLine5);
  }

  if (newValue.addressPostcode !== props.addressPostcode) {
    emit("update:address-postcode", newValue.addressPostcode);
  }

  if (newValue.addressLine6 !== props.addressLine6) {
    emit("update:address-line6", newValue.addressLine6);
  }

  showAddressCard.value = true;
};

const startAddressCapture = async (failedToRetrieve?: boolean) => {
  const result = await showModalDialog<Address, typeof BmAddressCaptureDialog>(
    BmAddressCaptureDialog,
    {
      props: {
        address: address.value,
        failedToRetrieve,
      },
    }
  );

  if (result.wasSuccessful && result.value) {
    emitChangedFields(result.value);
  }
};

const shouldShowAddressCard = computed(() => {
  if (!props.allowManualInput) {
    return false;
  }

  return showAddressCard.value;
});

const updateAddressFromRetrieve = (retrievedAddress: Address) => {
  emitChangedFields(retrievedAddress);
};

const clearAddress = () => {
  emitChangedFields({
    addressBusinessName: undefined,
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    addressLine4: "",
    addressLine5: "",
    addressPostcode: "",
    addressLine6: "",
  });
};

watch(
  address,
  () => {
    // If we have an address at all, show the full address card
    if (formatAddressForDisplay(address.value, "")) {
      showAddressCard.value = true;
    } else {
      showAddressCard.value = false;
    }
  },
  { immediate: true }
);

const addressIsEmpty = computed(() => {
  return formatAddressForDisplay(address.value) === "";
});

const dataComponent = dataComponentAttrs.bmAddress;
</script>

<template>
  <bm-address-lookup
    v-if="!shouldShowAddressCard"
    v-bind="$attrs"
    @address-retrieved="updateAddressFromRetrieve"
    @failed-to-retrieve-address="startAddressCapture(true)"
  >
    <template #append-outer>
      <bm-btn
        icon
        button-type="ghost"
        :data-component="dataComponent"
        :data-qa="
          ($attrs.dataQa?.toString() ||
            $attrs.t?.toString() ||
            $attrs.label?.toString() ||
            '') + '_edit_adddress'
        "
        @click="startAddressCapture()"
      >
        <bm-icon icon="Edit" />
      </bm-btn>
    </template>
  </bm-address-lookup>
  <bm-control
    v-else
    v-bind="$attrs"
    :read-only-value="formatAddressForDisplay(address)"
  >
    <template #default="{ dataQa }">
      <div :style="{ cursor: 'pointer' }" @click="startAddressCapture()">
        <v-container fluid pa-0>
          <v-row align="center" dense>
            <v-col :data-qa="dataQa">
              {{
                formatAddressForDisplay(address) ||
                $m.smart_address_click_to_add
              }}
            </v-col>
            <v-col cols="auto">
              <bm-btn
                small
                icon
                button-type="ghost"
                :data-component="dataComponent"
                :data-qa="(dataQa || '') + '_edit_adddress'"
              >
                <bm-icon icon="Edit" />
              </bm-btn>
            </v-col>
            <v-col v-if="!addressIsEmpty" cols="auto">
              <bm-btn small icon button-type="ghost" @click.stop="clearAddress">
                <bm-icon icon="Close" />
              </bm-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </template>
  </bm-control>
</template>
